import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Atalho de mensagem do Chat. `daEmpresa` = aparece para todos; senão só para quem o criou. */
export interface Atalho {
    atalhoId: number;
    comando: string;
    titulo: string;
    corpo: string;
    ativo: boolean;
    daEmpresa: boolean;
    usuarioId: number | null;
    anexoNome?: string | null;
    dataCriacao?: string;
    /** Inserções do atalho no compositor nos últimos 30 dias (a API agrega; conta inserção, não envio). */
    usos30Dias?: number;
    /** Pessoas distintas que inseriram o atalho nos últimos 30 dias. */
    pessoasUsaram30Dias?: number;
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

export interface CreateAtalhoDTO {
    comando: string;
    titulo: string;
    corpo: string;
    daEmpresa: boolean;
}

export interface UpdateAtalhoDTO {
    atalhoId: number;
    comando: string;
    titulo: string;
    corpo: string;
    ativo: boolean;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * A API recusa com 400 e a mensagem em português no corpo ("Já existe um atalho..."). Escrita devolve
 * esse corpo em vez de estourar, para a tela mostrar o motivo e não um erro genérico.
 */
const lerEscrita = async <T,>(response: Response, padrao: string): Promise<ApiResponse<T>> => {
    if (response.ok || response.status === 400) {
        try {
            return await response.json();
        } catch {
            if (response.ok) return { sucesso: true, mensagem: '', dados: undefined as T };
        }
    }
    throw new Error(padrao);
};

export const AtalhoService = {
    /** Os meus + os da empresa, numa lista só, com os meus primeiro. */
    getAll: async (): Promise<ApiResponse<Atalho[]>> => {
        const response = await apiFetch(`${API_URL}/api/Atalho/BuscarTodos`, {
            method: 'GET',
            headers: JSON_HEADERS,
        });
        if (!response.ok) {
            if (response.status === 400 || response.status === 404) {
                return { sucesso: true, mensagem: 'Sem atalhos', dados: [] };
            }
            throw new Error('Erro ao buscar atalhos');
        }
        return response.json();
    },

    getById: async (id: number): Promise<ApiResponse<Atalho>> => {
        const response = await apiFetch(`${API_URL}/api/Atalho/BuscarPorId/${id}`, {
            method: 'GET',
            headers: JSON_HEADERS,
        });
        if (!response.ok) throw new Error('Erro ao buscar atalho');
        return response.json();
    },

    create: async (atalho: CreateAtalhoDTO): Promise<ApiResponse<Atalho>> => {
        const response = await apiFetch(`${API_URL}/api/Atalho/Criar`, {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify(atalho),
        });
        return lerEscrita<Atalho>(response, 'Erro ao criar atalho');
    },

    update: async (atalho: UpdateAtalhoDTO): Promise<ApiResponse<null>> => {
        const response = await apiFetch(`${API_URL}/api/Atalho/Atualizar`, {
            method: 'PUT',
            headers: JSON_HEADERS,
            body: JSON.stringify(atalho),
        });
        return lerEscrita<null>(response, 'Erro ao atualizar atalho');
    },

    delete: async (id: number): Promise<ApiResponse<null>> => {
        const response = await apiFetch(`${API_URL}/api/Atalho/Remover/${id}`, {
            method: 'DELETE',
            headers: JSON_HEADERS,
        });
        return lerEscrita<null>(response, 'Erro ao excluir atalho');
    },

    /** Conta uma inserção do atalho no compositor. Falha calada: contagem nunca atrapalha o atendimento. */
    registrarUso: async (atalhoId: number, conversaId?: number | null): Promise<void> => {
        try {
            await apiFetch(`${API_URL}/api/Atalho/RegistrarUso`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({ atalhoId, conversaId: conversaId ?? null }),
            });
        } catch {
            /* sem efeito para quem atende */
        }
    },
};
